import type { RouterOutputs } from '@package/api'
import type { SeatSourceType } from '@package/drizzle/schema'
import { getDaysBetween } from '@helper/utils/date'
import { formatCurrency, formatDate } from '@helper/utils/format'

import { seatSourceLabel } from '../../lib/seat-source'

/** Which product the billing screen belongs to. Drives copy and checkout links only. */
export type BillingType = 'school' | 'web'

export type BillingInvoice = RouterOutputs['myOrganization']['getInvoices']['data'][number]

/** One seat pool: a membership plus its pro-rata add-ons, all sharing that membership's window. */
export type SeatPool = RouterOutputs['myOrganization']['getSeatPools'][number]

/** One organization covering this organization's children with seats it provisioned itself. */
export type ForeignCoverage = RouterOutputs['myOrganization']['getForeignPremiumCoverage'][number]

/**
 * How close a foreign seat's end date has to be before the notice warns instead of informing.
 *
 * Seven days, matching the "about to expire" window the owned-pool banner uses in
 * `reminder-expired-membership`. The two notices describe the same kind of loss and should not
 * disagree about when it becomes news — but they stay separate surfaces, because that cron walks
 * pools this organization owns and a foreign seat is in nobody's reminder here.
 */
export const FOREIGN_ACCESS_WARNING_DAYS = 7

/**
 * Names the organization behind a foreign seat, for copy like "dari sekolah SD Pelita Harapan".
 *
 * Falls back rather than printing an empty name: the seat's organization can be gone (the column is
 * `set null` on delete) or simply unnamed, and the access it grants is real either way — the
 * sentence has to still parse.
 */
export function foreignSourceLabel(coverage: ForeignCoverage): string {
  if (!coverage.organizationName) return 'organisasi lain'

  return coverage.isSchool ? `sekolah ${coverage.organizationName}` : coverage.organizationName
}

/** The word that follows "Premium" in every plan label: "Premium Sekolah" / "Premium Keluarga". */
export function planFlavor(type: BillingType): string {
  return type === 'school' ? 'Sekolah' : 'Keluarga'
}

export function invoiceStatusLabel(status: BillingInvoice['status']): string {
  if (status === 'PAID') return 'Lunas'
  if (status === 'EXPIRED') return 'Dibatalkan'
  return 'Belum dibayar'
}

/**
 * Short label for one row of Riwayat Tagihan — the plan the invoice paid for, not its status.
 * Falls back to the bare noun when the linked price is gone (price rows are `set null` on delete).
 */
export function invoiceTypeLabel(invoice: BillingInvoice): string {
  if (invoice.type === 'ADD-ON') return 'Tambah kursi'
  if (invoice.type === 'DIRECT_PAID') return 'Premium (langsung)'

  const prefix = invoice.type === 'RENEWAL' ? 'Perpanjangan' : 'Premium'
  const duration = invoice.price?.duration

  return duration ? `${prefix} ${duration} bulan` : prefix
}

/**
 * Heading of the charge block inside Detail Tagihan, e.g. "Biaya premium sekolah 12 bulan".
 * Add-ons keep the same heading — they extend the running plan rather than buy a new one.
 */
export function invoiceChargeTitle(invoice: BillingInvoice, type: BillingType): string {
  const flavor = planFlavor(type).toLowerCase()
  const duration = invoice.price?.duration

  return duration ? `Biaya premium ${flavor} ${duration} bulan` : `Biaya premium ${flavor}`
}

/**
 * Membership window an invoice paid for. Invoices created before the membership exists only carry
 * `customMembershipStartDate`, so both shapes are covered before giving up.
 */
export function invoicePeriod(invoice: BillingInvoice): string | null {
  const start = invoice.membershipStartDate ?? invoice.customMembershipStartDate
  const end = invoice.membershipEndDate

  if (!start || !end) return null

  return `${formatDate(start, { dateStyle: 'medium' })} - ${formatDate(end, { dateStyle: 'medium' })}`
}

/**
 * Sub-label under the seat line. Add-ons are billed pro-rata for the days left on the membership,
 * so they price per day instead of per seat.
 */
export function invoiceSeatBreakdown(invoice: BillingInvoice): string | null {
  if (invoice.type === 'ADD-ON') {
    const from = invoice.paidDate ?? invoice.createdAt

    if (!invoice.membershipEndDate) return null

    const { totalDays } = getDaysBetween(from, invoice.membershipEndDate)

    return `${formatCurrency(invoice.perSeatPrice)} x ${totalDays} sisa hari`
  }

  return `${formatCurrency(invoice.perSeatPrice)} per kursi`
}

/**
 * A renewal's pro-rata bridge: what its EMPTY seats cost for the days between payment and the
 * handover, on top of the term. Recovered from the invoice as everything `totalPrice` carries
 * beyond `perSeatPrice x seats x duration`; zero for every renewal made before empty seats started
 * at payment, and for any other invoice type.
 */
export function invoiceRenewalBridge(invoice: BillingInvoice): number {
  if (invoice.type !== 'RENEWAL' || !invoice.price) return 0

  return Math.max(0, invoice.totalPrice - invoice.perSeatPrice * invoice.seats * invoice.price.duration)
}

/** Seat line label: add-ons say "tambahan" so a top-up never reads as the whole plan. */
export function invoiceSeatLabel(invoice: BillingInvoice): string {
  return invoice.type === 'ADD-ON' ? `${invoice.seats} kursi tambahan` : `${invoice.seats} kursi`
}

/**
 * Everything a row shows, lowercased into one haystack. Search matches what the user can read —
 * the formatted date and the Indonesian labels — not the raw enum values behind them.
 */
export function invoiceSearchText(invoice: BillingInvoice): string {
  return [
    invoice.invoiceNumber,
    formatDate(invoice.createdAt, { dateStyle: 'medium' }),
    formatDate(invoice.createdAt, { dateStyle: 'long' }),
    formatCurrency(invoice.amountPaid),
    String(invoice.amountPaid),
    invoiceTypeLabel(invoice),
    invoiceStatusLabel(invoice.status),
  ]
    .join(' ')
    .toLowerCase()
}

/**
 * Names the seats in one pool, by the entitlement flavour behind them rather than by the
 * commercial/grant split alone — only `sponsored_scholarship` is a beasiswa, and a directly
 * provisioned or pilot pool said otherwise. Same wording as the school's "Kelola kursi" seat
 * picker, so a pool reads identically wherever it is named.
 */
export function poolSeatLabel(sourceType: SeatSourceType | null, isCommercial: boolean): string {
  return seatSourceLabel(sourceType, isCommercial)
}

/** Checkout destinations reachable from the billing header. */
export const checkoutPaths = {
  /** Start a paid plan. Where Starter upgrades go, and where a grant-only organization goes too:
   *  sponsor-funded seats cannot be extended, so its only route to a paid window is a new plan. */
  newPlan: '/checkout/new',
  /** Buy more seats onto an existing paid window. */
  addOn: '/checkout/add-on',
  /** Extend an existing paid window. */
  renewal: '/checkout/perpanjang',
} as const
